// Spintax: "{a|b}" expansion (nested, innermost-first) + photo variation helpers.

const INNER = /\{([^{}]*)\}/;

export function expandSpintax(input: string, rnd: () => number = Math.random): string {
  let t = input;
  let guard = 0;
  while (INNER.test(t) && guard++ < 10000) {
    t = t.replace(INNER, (_m, body: string) => {
      const opts = body.split("|");
      return opts[Math.floor(rnd() * opts.length)] ?? "";
    });
  }
  return t;
}

export interface SpintaxLint {
  ok: boolean;
  warnings: string[];
  errors: string[];
}

export function lintSpintax(input: string): SpintaxLint {
  const warnings: string[] = [];
  const errors: string[] = [];
  // brace balance
  let depth = 0;
  for (const ch of input) {
    if (ch === "{") depth++;
    else if (ch === "}") depth--;
    if (depth < 0) {
      errors.push("Llave de cierre '}' sin abrir.");
      break;
    }
  }
  if (depth > 0) errors.push("Falta cerrar una o más llaves '{'.");

  // empty / single-option groups
  const groups = input.match(/\{[^{}]*\}/g) ?? [];
  for (const g of groups) {
    const body = g.slice(1, -1);
    const opts = body.split("|");
    if (opts.some((o) => o.trim() === "")) warnings.push(`Grupo con opción vacía: ${g}`);
    if (opts.length < 2) warnings.push(`Grupo con una sola opción (no varía): ${g}`);
  }
  return { ok: errors.length === 0, warnings, errors };
}

function splitTopLevel(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = "";
  for (const ch of s) {
    if (ch === "{") depth++;
    else if (ch === "}") depth--;
    if (ch === "|" && depth === 0) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

/**
 * Number of distinct variants the template can produce.
 * variants(sequence) = product of token variants; variants(group) = sum of option variants.
 */
export function countVariants(input: string): number {
  let total = 1;
  let i = 0;
  while (i < input.length) {
    if (input[i] === "{") {
      let depth = 0;
      let j = i;
      for (; j < input.length; j++) {
        if (input[j] === "{") depth++;
        else if (input[j] === "}") {
          depth--;
          if (depth === 0) break;
        }
      }
      const inner = input.slice(i + 1, j);
      let sum = 0;
      for (const opt of splitTopLevel(inner)) sum += countVariants(opt);
      total *= sum || 1;
      if (total > Number.MAX_SAFE_INTEGER) return Number.MAX_SAFE_INTEGER;
      i = j + 1;
    } else {
      i++;
    }
  }
  return total;
}

/** Vary the file name to change the upload's identity. */
export function varyFileName(base: string, rnd: () => number = Math.random): string {
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : "";
  const suffix = Math.floor(rnd() * 1e9).toString(36);
  return `${stem}_${suffix}${ext}`;
}

/** Append a few random bytes to a blob so its hash differs per send (secondary lever). */
export function jitterBytes(blob: Blob, rnd: () => number = Math.random): Blob {
  const n = 1 + Math.floor(rnd() * 8);
  const extra = new Uint8Array(n);
  for (let i = 0; i < n; i++) extra[i] = Math.floor(rnd() * 256);
  return new Blob([blob, extra], { type: blob.type });
}
